  import { useState } from "react";
  import axios from "axios";
  import { motion, AnimatePresence } from "framer-motion";
  import { Phone, Mail, Globe, MapPin, Map, Clock, ArrowRight, CheckCircle2 } from "lucide-react";
  import { useSeo } from "@/lib/useSeo";
  import { PageHero, SectionLabel } from "@/components/site/Bits";
  import { Reveal } from "@/components/site/Reveal";
  import { CONTACT } from "@/data/content";
  import { Input } from "@/components/ui/input";
  import { Textarea } from "@/components/ui/textarea";
  import { Label } from "@/components/ui/label";
  import { Checkbox } from "@/components/ui/checkbox";
  import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  } from "@/components/ui/select";
  import { toast } from "@/components/ui/sonner";
  {/*`${BACKEND_URL}/api`;*/}
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = BACKEND_URL;

  const ENQUIRY_OPTIONS = [
    "Consultancy partnership — Southampton",
    "Consultancy partnership — London",
    "Consultancy partnership — Both cities",
    "Other",
  ];
  const ROLE_TYPES = ["Part-time", "Seasonal", "Full-time", "Fixed-term", "Permanent"];

  const DETAILS = [
    { icon: Phone, label: "Mobile", value: CONTACT.phone, href: CONTACT.phoneHref },
    { icon: Globe, label: "Website", value: CONTACT.website, href: `https://${CONTACT.website}` },
    { icon: MapPin, label: "Location", value: CONTACT.location },
    { icon: Map, label: "Coverage", value: CONTACT.coverage },
  ];

  export default function Contact() {
    useSeo(
      "Contact UK Student Job — International Candidate Employment Partner for Consultancies | Southampton · London",
      "Get in touch with UK Student Job. We connect an abundant pool of pre-verified international candidates from Southampton's two universities with job consultancies across Southampton and London. All role types. We respond within one business day."
    );

    const [form, setForm] = useState({
      first_name: "", last_name: "", email: "", organisation: "", enquiry_type: "", message: "",
    });
    const [roleTypes, setRoleTypes] = useState([]);
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    const set = (k, v) => {
      setForm((f) => ({ ...f, [k]: v }));
      setErrors((e) => ({ ...e, [k]: undefined }));
    };

    const toggleRole = (r) =>
      setRoleTypes((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));

    const validate = () => {
      const e = {};
      if (!form.first_name.trim()) e.first_name = "Required";
      if (!form.last_name.trim()) e.last_name = "Required";
      if (!form.email.trim()) e.email = "Required";
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
      if (!form.organisation.trim()) e.organisation = "Required";
      if (!form.enquiry_type) e.enquiry_type = "Please select an option";
      if (!form.message.trim()) e.message = "Required";
      setErrors(e);
      return Object.keys(e).length === 0;
    };

    const onSubmit = async (ev) => {
      ev.preventDefault();
      if (!validate()) {
        toast.error("Please complete the required fields.");
        return;
      }
      setSubmitting(true);
      try {
        await axios.post(`${API}?route=contact`, { ...form, role_types: roleTypes }, {
  headers: { "Content-Type": "application/json" }
});

        setDone(true);
        toast.success("Message sent — we'll respond within one business day.");
      } catch (err) {
        toast.error("Something went wrong. Please try again or email us directly.");
      } finally {
        setSubmitting(false);
      }
    };

    const errCls = (k) => (errors[k] ? "border-[#C8102E] focus-visible:ring-[#C8102E]" : "");

    return (
      <>
        <PageHero
          testId="contact-hero"
          label="Get in touch"
          lines={[<>Let's build this</>, <span key="r" className="text-[#C8102E]">partnership</span>, <>together</>]}
          sub="If you are a job consultancy looking for a compliant, pre-verified international talent pool across Southampton or London — for any role type — complete the form and we will respond within one business day."
        />

        <section className="bg-[#F4F6F9] py-20 lg:py-28" data-testid="contact-main">
          <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-8 items-start">
            {/* Details */}
            <div className="lg:col-span-5">
              <Reveal>
                <div className="bg-[#0A1F44] rounded-[1.75rem] p-9 lg:p-10 text-white relative overflow-hidden grain">
                  <div className="absolute inset-0 flag-stripes opacity-25" />
                  <div className="relative">
                    <SectionLabel light>Contact details</SectionLabel>
                    <h2 className="mt-5 font-outfit font-extrabold text-2xl lg:text-3xl">Talk to our partnerships team</h2>

                    <div className="mt-8 space-y-5">
                      {DETAILS.map((d) => (
                        <div key={d.label} className="flex items-start gap-4">
                          <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 text-[#C8102E] shrink-0">
                            <d.icon className="h-5 w-5" />
                          </div>
                          <div>
                            <div className="text-xs uppercase tracking-wider text-white/50 font-semibold">{d.label}</div>
                            {d.href ? (
                              <a href={d.href} className="text-white font-manrope font-semibold hover:text-[#C8102E] transition-colors">{d.value}</a>
                            ) : (
                              <div className="text-white font-manrope font-semibold">{d.value}</div>
                            )}
                          </div>
                        </div>
                      ))}

                    {/* Emails — two clean aligned items, no ampersand */}
                    <div className="flex items-start gap-4">
                      <div className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-white/10 text-[#C8102E] shrink-0">
                        <Mail className="h-5 w-5" />
                      </div>
                      <div>
                        <div className="text-xs uppercase tracking-wider text-white/50 font-semibold">Email</div>
                        <div className="mt-1 space-y-1">
                          <a href={`mailto:${CONTACT.emailBusiness}`} className="block text-white font-manrope font-semibold hover:text-[#C8102E] transition-colors break-all">{CONTACT.emailBusiness}</a>
                          <a href={`mailto:${CONTACT.emailInfo}`} className="block text-white font-manrope font-semibold hover:text-[#C8102E] transition-colors break-all">{CONTACT.emailInfo}</a>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-8 pt-6 border-t border-white/10 flex items-start gap-3">
                    <Clock className="h-5 w-5 text-[#C8102E] mt-0.5 shrink-0" />
                    <p className="text-white/70 text-sm leading-relaxed font-manrope">
                      We respond to every consultancy enquiry within one business day. For urgent
                      placement needs, please indicate this in your message and we will prioritise
                      your request.
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <Reveal delay={0.08}>
              <div className="bg-white rounded-[1.75rem] p-8 lg:p-10 border border-[#0A1F44]/8 shadow-[0_8px_32px_rgba(10,31,68,0.06)]">
                <AnimatePresence mode="wait">
                  {done ? (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                      className="text-center py-16"
                      data-testid="contact-success"
                    >
                      <div className="inline-flex h-20 w-20 items-center justify-center rounded-full bg-[#C8102E]/10 text-[#C8102E]">
                        <CheckCircle2 className="h-10 w-10" />
                      </div>
                      <h3 className="mt-6 font-outfit font-extrabold text-2xl text-[#0A1F44]">Message received</h3>
                      <p className="mt-3 text-[#12294D]/70 font-manrope max-w-md mx-auto">
                        Thank you for reaching out. Our partnerships team will respond to your enquiry
                        within one business day.
                      </p>
                      <button
                        onClick={() => { setDone(false); setForm({ first_name: "", last_name: "", email: "", organisation: "", enquiry_type: "", message: "" }); setRoleTypes([]); }}
                        className="mt-8 inline-flex items-center gap-2 text-[#C8102E] font-semibold border-b-2 border-[#C8102E] pb-1"
                        data-testid="contact-reset"
                      >
                        Send another message
                      </button>
                    </motion.div>
                  ) : (
                    <motion.form
                      key="form"
                      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                      onSubmit={onSubmit}
                      className="space-y-6"
                      data-testid="contact-form"
                      noValidate
                    >
                      <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                          <Label className="text-[#0A1F44] font-semibold">First name *</Label>
                          <Input data-testid="contact-first-name" value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className={`mt-2 h-11 ${errCls("first_name")}`} placeholder="Jane" />
                          {errors.first_name && <p className="mt-1 text-xs text-[#C8102E]">{errors.first_name}</p>}
                        </div>
                        <div>
                          <Label className="text-[#0A1F44] font-semibold">Last name *</Label>
                          <Input data-testid="contact-last-name" value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className={`mt-2 h-11 ${errCls("last_name")}`} placeholder="Doe" />
                          {errors.last_name && <p className="mt-1 text-xs text-[#C8102E]">{errors.last_name}</p>}
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                          <Label className="text-[#0A1F44] font-semibold">Email address *</Label>
                          <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={`mt-2 h-11 ${errCls("email")}`} placeholder="jane@consultancy.com" />
                          {errors.email && <p className="mt-1 text-xs text-[#C8102E]">{errors.email}</p>}
                        </div>
                        <div>
                          <Label className="text-[#0A1F44] font-semibold">Consultancy / organisation name *</Label>
                          <Input data-testid="contact-organisation" value={form.organisation} onChange={(e) => set("organisation", e.target.value)} className={`mt-2 h-11 ${errCls("organisation")}`} placeholder="Acme Recruitment Ltd" />
                          {errors.organisation && <p className="mt-1 text-xs text-[#C8102E]">{errors.organisation}</p>}
                        </div>
                      </div>

                      <div>
                        <Label className="text-[#0A1F44] font-semibold">I am enquiring about *</Label>
                        <Select value={form.enquiry_type} onValueChange={(v) => set("enquiry_type", v)}>
                          <SelectTrigger data-testid="contact-enquiry-type" className={`mt-2 h-11 ${errCls("enquiry_type")}`}>
                            <SelectValue placeholder="Select an option" />
                          </SelectTrigger>
                          <SelectContent>
                            {ENQUIRY_OPTIONS.map((o) => (
                              <SelectItem key={o} value={o} data-testid={`enquiry-opt-${o}`}>{o}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {errors.enquiry_type && <p className="mt-1 text-xs text-[#C8102E]">{errors.enquiry_type}</p>}
                      </div>

                      <div>
                        <Label className="text-[#0A1F44] font-semibold">Role types needed</Label>
                        <div className="mt-3 flex flex-wrap gap-3">
                          {ROLE_TYPES.map((r) => {
                            const active = roleTypes.includes(r);
                            return (
                              <label
                                key={r}
                                data-testid={`role-${r}`}
                                className={`flex items-center gap-2 px-4 py-2.5 rounded-full border cursor-pointer transition-colors duration-200 font-manrope text-sm font-semibold ${active ? "bg-[#0A1F44] text-white border-[#0A1F44]" : "bg-white text-[#0A1F44] border-[#0A1F44]/20 hover:border-[#0A1F44]"}`}
                              >
                                <Checkbox checked={active} onCheckedChange={() => toggleRole(r)} className={active ? "border-white data-[state=checked]:bg-white data-[state=checked]:text-[#0A1F44]" : ""} />
                                {r}
                              </label>
                            );
                          })}
                        </div>
                      </div>

                      <div>
                        <Label className="text-[#0A1F44] font-semibold">Your message *</Label>
                        <Textarea
                          data-testid="contact-message"
                          value={form.message}
                          onChange={(e) => set("message", e.target.value)}
                          rows={5}
                          className={`mt-2 ${errCls("message")}`}
                          placeholder="Tell us about your consultancy, the sectors your clients operate in, the type of roles you need to fill, and the cities you are covering…"
                        />
                        {errors.message && <p className="mt-1 text-xs text-[#C8102E]">{errors.message}</p>}
                      </div>

                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid="contact-submit"
                        className="group inline-flex items-center justify-center gap-2 bg-[#C8102E] text-white font-manrope font-semibold px-8 py-4 rounded-full transition-colors duration-300 hover:bg-[#A00B24] disabled:opacity-60"
                      >
                        {submitting ? "Sending…" : "Send message"}
                        <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
                      </button>

                      <p className="text-xs text-[#12294D]/50 font-manrope">
                        By submitting this form you agree to our Privacy Policy. We will use your
                        details only to respond to your enquiry.
                      </p>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
